import React, { useContext, useRef, useState } from 'react';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { CollectionInvoicePdf } from '../model';
import { filenameContainsInvoiceNumber } from '../utils/invoiceFilename';

export interface InvoicePdfUploadResult {
  applyTo: string;
  acctNo: string;
  pdf: CollectionInvoicePdf;
  /**
   * The name the admin picked. The server renames every upload to Invoice-<apply_to>.pdf, so
   * this is the only place the original name survives — the Missing Invoices dialog shows both.
   */
  pickedFilename: string;
}

interface UseInvoicePdfUploadArgs {
  location: string;
  /** Omit on pages whose rows span several accounts; pass the account to triggerUpload instead. */
  acctNo?: string;
  onUploaded?: (result: InvoicePdfUploadResult) => void;
}

export function useInvoicePdfUpload({ location, acctNo, onUploaded }: UseInvoicePdfUploadArgs) {
  const { showErrorSnackBar } = useContext(SnackBarContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<{ applyTo: string; acctNo: string } | null>(null);
  const [uploadingApplyTo, setUploadingApplyTo] = useState<string | null>(null);
  const canUpload = Boolean(location) && (acctNo === undefined || Boolean(acctNo));

  const triggerUpload = (applyTo: string, rowAcctNo?: string) => {
    const resolvedAcctNo = rowAcctNo ?? acctNo;
    if (!resolvedAcctNo) return;
    pendingRef.current = { applyTo, acctNo: resolvedAcctNo };
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const pending = pendingRef.current;
    e.target.value = '';
    pendingRef.current = null;
    if (!file || !pending || !location) return;

    const { applyTo, acctNo: rowAcctNo } = pending;

    if (!filenameContainsInvoiceNumber(file.name, applyTo)) {
      window.alert(`Filename must include the invoice number (${applyTo}). Upload cancelled.`);
      return;
    }

    setUploadingApplyTo(applyTo);
    const formData = new FormData();
    formData.append('pdf', file);
    // location is always explicit: the same invoice number can exist in both locations.
    formData.append('location', location);
    formData.append('acct_no', rowAcctNo);
    formData.append('apply_to', applyTo);

    try {
      const res = await axios.post('/collections/edoc_invoice_imports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = res.data as { pdf?: CollectionInvoicePdf } | undefined;
      if (!payload?.pdf) {
        showErrorSnackBar?.('Failed to upload PDF');
        return;
      }
      onUploaded?.({ applyTo, acctNo: rowAcctNo, pdf: payload.pdf, pickedFilename: file.name });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to upload PDF';
      showErrorSnackBar?.(msg);
    } finally {
      setUploadingApplyTo(null);
    }
  };

  return { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload };
}
